import { graphql, PageProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';
import Image from 'src/elements/image/image';
import Layout from 'src/elements/layout/layout';
import LinkBranded from 'src/elements/links/Link.branded';
import Heading from 'src/elements/typography/heading';
import { formatElementFromHtmlAst, MarkupElement } from 'src/functions/formatElement.htmlAst';
import CaseStudyCarousel from 'src/organisms/articles/caseStudies.carousel';
import CaseStudyHighlight from 'src/organisms/articles/caseStudies/caseStudies.highlight';
import HighlightMetrics from 'src/organisms/articles/caseStudies/caseStudies.highlightMetrics';
import SocialMediaTopBarLinks from 'src/organisms/layout/SocialMediaTopBarLinks';
import {
  CaseStudiesFooter, MoreStoriesContainer,
  TemplateAction,
  TemplateBody,
  TemplateContainer,
  TemplateHeadlineTitle,
  TemplateImage,
} from './caseStudies.styles';
import { TemplateTitle } from './caseStudies.styles';
import { TemplateContent } from './caseStudies.styles';


interface CaseSudiesProps extends PageProps {
  data: {
    contentfulCaseStudy: {
      id: string;
      title: string;
      subtitle?: string;
      slug: string;
      seoTitle?: string;
      seoDescription?: string;
      contentful_id: string;
      launchDate: string;
      members: string;
      pricing: string;
      heroImage: {
        gatsbyImageData: IGatsbyImageData;
        file?: {
          url: string;
        }
      };
      body: {
        childMarkdownRemark: {
          htmlAst: {
            type: string;
            children: MarkupElement[]
          }
        }
      }
      highlight: {
        childMarkdownRemark: {
          htmlAst: {
            type: string;
            children: MarkupElement[]
          }
        }
      }
    }
    allContentfulCaseStudy: {
      edges: {
        node: {
          title: string;
          slug: string;
          heroImage: { gatsbyImageData: IGatsbyImageData };
        }
      }[]
    }
  }
}

const CaseStudiesTemplate = (props: CaseSudiesProps): JSX.Element => {
  const caseStudy = props.data.contentfulCaseStudy;
  const posts = props.data.allContentfulCaseStudy;

  const titleFromSlug = props.data.contentfulCaseStudy.slug
    .split('-')
    .join(' ')
    .replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());

  return (
    <Layout
      helmet={{
        title: caseStudy.seoTitle || caseStudy.title,
        description: caseStudy.seoDescription,
        previewImageURL: caseStudy.heroImage?.file?.url
      }}
      topBarLinkSection={<SocialMediaTopBarLinks path={`/case-studies/${caseStudy.slug}`} title={caseStudy.title}/>}
      location={props.location}
      expandFooter
    >
      <TemplateContainer>
        <TemplateTitle>
          <Heading
            text={caseStudy.title}
            size="3.375em"
            sizeLg="2.5em"
            sizeSubLg="2em"
            sizeSubMd="1.5em"
            align="center"
          />
        </TemplateTitle>
        <TemplateImage>
          <Image
            childImageSharp={caseStudy.heroImage.gatsbyImageData}
            alt={caseStudy.title}
          />
        </TemplateImage>
        <TemplateHeadlineTitle>
          <Heading
            text={caseStudy.subtitle ?? `Featured in this Story of Awesome: ${titleFromSlug}`}
            size="2.5em"
            sizeLg="2em"
            sizeSubLg="1.5em"
            align="center"
          />
        </TemplateHeadlineTitle>
        <TemplateBody>
          <HighlightMetrics
            launchDate={caseStudy.launchDate}
            members={caseStudy.members}
            pricing={caseStudy.pricing}
          />
          <CaseStudyHighlight highlight={caseStudy.highlight} />
          <TemplateContent>
            {caseStudy.body.childMarkdownRemark.htmlAst.children.map((child, index) => formatElementFromHtmlAst(child, `${index}`))}
          </TemplateContent>
          <TemplateAction>
            <LinkBranded
              url={'https://new.mn.co/?plan=free-trial'}
              text="Start Your Own Mighty Network Today!"
              justify="center"
              weight="normal"
              size="1em"
            />
          </TemplateAction>
        </TemplateBody>
      </TemplateContainer>
      <MoreStoriesContainer>
        <Heading
          text="Read More Stories of Awesome"
          align="center"
          size="2.5rem"
          sizeSubLg="2rem"
          sizeMd="1.5rem"
        />
        <CaseStudyCarousel
          posts={posts.edges
            .filter(e => e.node.slug !== caseStudy.slug)
            .map(({ node }) => ({
              title: node.title,
              image: node.heroImage.gatsbyImageData,
              slug: node.slug,
            }))}
        />
      </MoreStoriesContainer>
      <CaseStudiesFooter>
        <Heading
          text="Mighty Networks: where people come for your content, but stay for your community."
          size="1.5rem"
          sizeMd="1.2rem"
        />
        <LinkBranded text="Start Your 14-Day Free Trial" url="https://new.mn.co?plan=free-trial" captionJustify="center"/>
      </CaseStudiesFooter>
    </Layout>
  )
}

export default CaseStudiesTemplate;

export const pageQuery = graphql`
  query CaseStudyBySlug($slug: String!) {
    contentfulCaseStudy(slug: { eq: $slug }) {
      id
      title
      subtitle
      seoTitle
      seoDescription
      slug
      launchDate
      members
      pricing
      title
      slug
      contentful_id
      body {
        childMarkdownRemark {
          htmlAst
        }
      }
      highlight {
        childMarkdownRemark {
          htmlAst
        }
      }
      heroImage {
        file {
          url
        }
        gatsbyImageData(width: 960, layout: CONSTRAINED, placeholder: BLURRED)
      }
    }
    allContentfulCaseStudy {
      edges {
        node {
          title
          slug
          heroImage {
            gatsbyImageData(width: 840, layout: CONSTRAINED, placeholder: BLURRED)
          }
        }
      }
    }
  }
`
